import { TextField } from "@mui/material";
import { isEqual } from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { LoggerService } from "@/shared/services";
import { TopicData, useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  Position,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  FileSelector,
  PathSelector,
} from "../../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../../Panel/predicates";
import {
  useDataFiles,
  useFileIdToTopics,
  useWorkspaceFiles,
} from "../../../../WorkspaceCtx/hooks";
import { ConfigItem } from "../../ConfigItem";
import styles from "../EntityConfig.module.css";

import { classifyPositionChange } from "./positionChangeDetection";
import { PositionPresetSelector } from "./PositionPresetSelector";
import { getFileAndPath, topicDataFor } from "./positionTopicData";
import { presetFor } from "./presets";
import {
  type AxisName,
  reconcileAxisPathsToFile,
} from "./reconcileAxisPathsToFile";
import { resolveAxesFileSelection } from "./resolveAxesFileSelection";

interface EntityConfigProps {
  entity: Entity;
  panelId: string;
}

export function PositionConfig({ entity, panelId }: EntityConfigProps) {
  const dispatch = useVizDispatch();
  const availableFiles = useDataFiles();
  const fileIdToTopics = useFileIdToTopics();

  // Membership check for the dangling-binding test uses the full workspace
  // file list, not `availableFiles` (which filters to files with topics). A
  // file in the workspace whose topics have not yet been fetched would
  // otherwise be misclassified as dangling at T=0 and not at T=final. The
  // FileSelector dropdown still uses `availableFiles` because only
  // data-bearing files are valid picks.
  const workspaceFiles = useWorkspaceFiles();

  // Resolve the file shown in the FileSelector. Bound axes derive the file
  // implicitly. When no axes are bound (e.g., right after picking a preset),
  // the user's last FileSelector pick is held in `scopingFile` so the topic
  // tree shown to PathSelectors stays in scope until they bind a path. In a
  // single-file workspace, FileSelector hides itself; fall back to the sole
  // file so PathSelectors still have options.
  const fileSelection = resolveAxesFileSelection(
    entity.position,
    workspaceFiles,
  );
  const [scopingFile, setScopingFile] = React.useState<string | undefined>(
    undefined,
  );
  const effectiveFileId =
    fileSelection.fileId ??
    scopingFile ??
    (availableFiles.length === 1 && !fileSelection.dangling
      ? availableFiles[0].file_id
      : undefined);
  const topicTree = effectiveFileId
    ? fileIdToTopics[effectiveFileId] ?? []
    : [];

  // Track the most recent position, inclusive of updates originating from this component.
  // This is used to detect position updates that originated in other components or from
  // layout/workspace loads.
  const positionRef = React.useRef(entity.position);

  // True while a dispatch is in-flight (entity.position hasn't caught up yet).
  // Prevents intermediate re-renders from being misclassified as external changes.
  const pendingDispatchRef = React.useRef(false);

  // A key used to reload the path-selector block to match external updates
  // or file changes. The inner PathSelector components capture their initial
  // input value on mount; bumping this key forces them to remount with the
  // new file's topic tree and the current paths pre-populated.
  const positionReloadKeyRef = React.useRef(0);

  // TODO(dave): look at using react-hook-form to manage all this state.
  // Position state
  const [forwardData, setForwardData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.position?.forward),
  );
  const [forwardScale, setForwardScale] = React.useState(
    `${entity.position?.forwardScale ?? 1}`,
  );
  const [forwardScaleError, setForwardScaleError] = React.useState("");

  const [rightData, setRightData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.position?.right),
  );
  const [rightScale, setRightScale] = React.useState(
    `${entity.position?.rightScale ?? 1}`,
  );
  const [rightScaleError, setRightScaleError] = React.useState("");

  const [upData, setUpData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.position?.up),
  );
  const [upScale, setUpScale] = React.useState(
    `${entity.position?.upScale ?? 1}`,
  );
  const [upScaleError, setUpScaleError] = React.useState("");
  const parseScaleInput = (
    value: string,
  ):
    | { ok: true; value: number; error?: undefined }
    | { ok: false; error: string } => {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return { ok: true, value: parsed };
    }
    return { ok: false, error: "Scale must be a number." };
  };

  const forwardPath = getFileAndPath(entity.position?.forward)?.path;
  const rightPath = getFileAndPath(entity.position?.right)?.path;
  const upPath = getFileAndPath(entity.position?.up)?.path;

  // File-change handler: reconcile existing axis paths against the new file's
  // topic tree and dispatch the new position. Paths whose structural match
  // exists in the new file are preserved (re-bound to the new file's
  // TopicData); paths that do not resolve are reset to 0. The Position's
  // file is implicit in its axis bindings — there is no entity-level file to
  // update separately.
  const onFileChange = React.useCallback(
    (newFileId: string) => {
      // Hold the user's pick so the topic tree stays scoped to it even when
      // reconciliation clears every axis (no derived file).
      setScopingFile(newFileId);

      const newFileTopicTree = fileIdToTopics[newFileId] ?? [];
      const preserved = reconcileAxisPathsToFile(
        entity.position,
        newFileTopicTree,
      );

      const reconciled: Position = {
        basePreset: entity.position?.basePreset,
        transformation: entity.position?.transformation,
        forward: 0,
        forwardScale: entity.position?.forwardScale ?? 1,
        right: 0,
        rightScale: entity.position?.rightScale ?? 1,
        up: 0,
        upScale: entity.position?.upScale ?? 1,
      };
      const preservedData: Partial<Record<AxisName, TopicData>> = {};
      for (const { axis, node } of preserved) {
        try {
          const topicData = node.toTopicData();
          reconciled[axis] = topicData;
          preservedData[axis] = topicData;
        } catch (err) {
          // Missing representation — treat as cleared. reconciled[axis] stays 0.
          LoggerService.warn(
            `ThreeD: could not rebind ${axis} axis on file change`,
            { path: node.data.message_path, err },
          );
        }
      }

      // Sync local form state to the reconciled result so the effect's
      // classifier sees consistency on the next render rather than stale
      // TopicData that would trigger a spurious re-dispatch.
      setForwardData(preservedData.forward);
      setRightData(preservedData.right);
      setUpData(preservedData.up);

      // Pre-sync classifier refs: this dispatch originates here, and we want
      // the next render to land on "none" (nothing left to do) rather than
      // classifying the reconciled entity.position as an external change.
      positionRef.current = reconciled;
      pendingDispatchRef.current = true;

      // Force PathSelectors to remount so they pick up the new file's topic
      // tree and the current paths (which will be empty for cleared axes).
      positionReloadKeyRef.current++;

      dispatch(
        updateThreeDEntity(panelId, entity.id, {
          position: reconciled,
        }),
      );
    },
    [entity.position, entity.id, panelId, dispatch, fileIdToTopics],
  );

  React.useEffect(
    function onPositionFieldsChanged() {
      // Build what the local form state would produce, for comparison.
      const parsedForwardScale = parseScaleInput(forwardScale);
      const parsedRightScale = parseScaleInput(rightScale);
      const parsedUpScale = parseScaleInput(upScale);
      setForwardScaleError(parsedForwardScale.error ?? "");
      setRightScaleError(parsedRightScale.error ?? "");
      setUpScaleError(parsedUpScale.error ?? "");

      const localPosition: Position | undefined =
        parsedForwardScale.ok && parsedRightScale.ok && parsedUpScale.ok
          ? {
              basePreset: entity.position?.basePreset,
              transformation: entity.position?.transformation,
              forward: forwardData ?? entity.position?.forward ?? 0,
              forwardScale: parsedForwardScale.value,
              right: rightData ?? entity.position?.right ?? 0,
              rightScale: parsedRightScale.value,
              up: upData ?? entity.position?.up ?? 0,
              upScale: parsedUpScale.value,
            }
          : undefined;

      const origin = classifyPositionChange(
        positionRef.current,
        entity.position,
        pendingDispatchRef.current,
        localPosition,
      );

      if (origin === "external") {
        // entity.position was changed without this component's involvement.
        positionRef.current = entity.position;
        pendingDispatchRef.current = false;
        setForwardScale(`${entity.position?.forwardScale ?? ""}`);
        setRightScale(`${entity.position?.rightScale ?? ""}`);
        setUpScale(`${entity.position?.upScale ?? ""}`);

        setForwardData(topicDataFor(entity.position?.forward));
        setRightData(topicDataFor(entity.position?.right));
        setUpData(topicDataFor(entity.position?.up));
        positionReloadKeyRef.current++;
        return;
      }

      // Clear pending flag once entity catches up with our dispatch.
      if (
        pendingDispatchRef.current &&
        isEqual(positionRef.current, entity.position)
      ) {
        pendingDispatchRef.current = false;
      }

      if (origin === "internal" && localPosition) {
        positionRef.current = localPosition;
        pendingDispatchRef.current = true;
        dispatch(
          updateThreeDEntity(panelId, entity.id, {
            position: localPosition,
          }),
        );
      }
    },
    [
      forwardData,
      rightData,
      upData,
      forwardScale,
      rightScale,
      upScale,
      entity.position,
      entity.id,
      panelId,
      dispatch,
    ],
  );

  const presetLabels = presetFor(entity.position)?.labels;

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Position
      </RobotoTypography>
      <div className={styles.container}>
        <PositionPresetSelector entity={entity} panelId={panelId} />
        {entity.position?.basePreset && (
          <>
            {(availableFiles.length >= 2 || fileSelection.dangling) && (
              <ConfigItem label="File">
                <FileSelector
                  className={styles.blockSpacing}
                  files={availableFiles}
                  selection={effectiveFileId}
                  onSelect={onFileChange}
                  isError={fileSelection.dangling}
                  forceShow={fileSelection.dangling}
                  helperText={
                    fileSelection.dangling
                      ? "The file this entity was bound to is not in the current workspace. Pick a file to continue."
                      : undefined
                  }
                />
              </ConfigItem>
            )}
            <div key={positionReloadKeyRef.current}>
              <ConfigItem label={`${presetLabels?.right ?? "Right"} Axis`}>
                <PathSelector
                  className={styles.blockSpacing}
                  initialInputValue={rightPath}
                  onChange={(node) => setRightData(node.toTopicData())}
                  options={topicTree}
                  // TODO(ENG-1672): use the 3d panel's viewability filter here
                  // once there's a filter to drag and drop message paths in.
                  optionFilter={isViewableAsPlot}
                />
              </ConfigItem>
              <ConfigItem label={`${presetLabels?.forward ?? "Forward"} Axis`}>
                <PathSelector
                  className={styles.blockSpacing}
                  initialInputValue={forwardPath}
                  onChange={(node) => setForwardData(node.toTopicData())}
                  options={topicTree}
                  optionFilter={isViewableAsPlot}
                />
              </ConfigItem>
              <ConfigItem label={`${presetLabels?.up ?? "Up"} Axis`}>
                <PathSelector
                  className={styles.blockSpacing}
                  initialInputValue={upPath}
                  onChange={(node) => setUpData(node.toTopicData())}
                  options={topicTree}
                  optionFilter={isViewableAsPlot}
                />
              </ConfigItem>
            </div>
            <div className={styles.blockSpacing}>
              <ConfigItem label="Scaling">
                <div className={styles.fieldRow}>
                  <TextField
                    label={`${presetLabels?.right ?? "Right"} Scale`}
                    type="number"
                    size="small"
                    margin="dense"
                    value={rightScale}
                    error={!!rightScaleError}
                    helperText={rightScaleError}
                    onChange={(e) => setRightScale(e.target.value)}
                    InputLabelProps={{ className: styles.input }}
                    InputProps={{ className: styles.input }}
                    inputProps={{ step: 0.1, className: styles.input }}
                    className={styles.scaleField}
                  />
                  <TextField
                    label={`${presetLabels?.forward ?? "Forward"} Scale`}
                    type="number"
                    size="small"
                    margin="dense"
                    value={forwardScale}
                    error={!!forwardScaleError}
                    helperText={forwardScaleError}
                    onChange={(e) => setForwardScale(e.target.value)}
                    InputLabelProps={{ className: styles.input }}
                    InputProps={{ className: styles.input }}
                    inputProps={{ step: 0.1, className: styles.input }}
                    className={styles.scaleField}
                  />
                  <TextField
                    label={`${presetLabels?.up ?? "Up"} Scale`}
                    type="number"
                    size="small"
                    margin="dense"
                    value={upScale}
                    error={!!upScaleError}
                    helperText={upScaleError}
                    onChange={(e) => setUpScale(e.target.value)}
                    InputLabelProps={{ className: styles.input }}
                    InputProps={{ className: styles.input }}
                    inputProps={{ step: 0.1, className: styles.input }}
                    className={styles.scaleField}
                  />
                </div>
              </ConfigItem>
            </div>
          </>
        )}
      </div>
    </>
  );
}
